import { PureComponent } from "react";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import { formatCreditAmount } from "./ai-chatbot-utils";

// Accessible name for the dialog (aria-labelledby). The modal never unmounts while the
// credits panel is alive (it just returns null when closed), so a single static id is safe —
// there is only ever one instance of this dialog per page.
const TITLE_ID = "kuika-ai-chatbot-success-modal-title";

// ── Confetti particles ────────────────────────────────────────────────
// Pure-CSS confetti: 60 particles with randomized color / horizontal drift /
// delay. Tight, soft palette (2s fall) — matches the success tone without
// feeling obnoxious. No third-party deps.

const CONFETTI_COUNT = 60;
const CONFETTI_PALETTE = [
  "var(--blue-5)",
  "var(--blue-4)",
  "var(--purple-5)",
  "var(--green-5)",
  "var(--yellow-5)",
  "var(--pink-5)"
];

interface ConfettiPieceStyle extends React.CSSProperties {
  "--dx"?: string;
}

function buildConfetti(): ConfettiPieceStyle[] {
  const out: ConfettiPieceStyle[] = [];
  for (let i = 0; i < CONFETTI_COUNT; i++) {
    const left = Math.random() * 100;
    const dx = (Math.random() - 0.5) * 300;
    const color = CONFETTI_PALETTE[i % CONFETTI_PALETTE.length];
    const delay = Math.random() * 0.4;
    const duration = 1.8 + Math.random() * 0.9;
    const rotate = Math.random() * 360;
    out.push({
      left: `${left}%`,
      background: color,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
      transform: `rotate(${rotate}deg)`,
      "--dx": `${dx}px`
    });
  }
  return out;
}

interface Props {
  isOpen: boolean;
  addedCredits: number;
  newBalance: number | null;
  onClose: () => void;
}

interface State {
  confetti: ConfettiPieceStyle[];
}

export class AiChatbotSuccessModal extends PureComponent<Props, State> {
  state: State = { confetti: [] };

  componentDidMount(): void {
    if (this.props.isOpen) {
      this.setState({ confetti: buildConfetti() });
      document.addEventListener("keydown", this.handleKeyDown);
    }
  }

  componentDidUpdate(prev: Props): void {
    if (!prev.isOpen && this.props.isOpen) {
      this.setState({ confetti: buildConfetti() });
      document.addEventListener("keydown", this.handleKeyDown);
    } else if (prev.isOpen && !this.props.isOpen) {
      document.removeEventListener("keydown", this.handleKeyDown);
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.props.onClose();
    }
  };

  render() {
    const { isOpen, addedCredits, newBalance, onClose } = this.props;
    if (!isOpen) return null;

    return (
      <div className="kuika-ai-chatbot-credits-success__overlay" onClick={onClose}>
        <div
          className="kuika-ai-chatbot-credits-success__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={TITLE_ID}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="kuika-ai-chatbot-credits-success__confetti" aria-hidden="true">
            {this.state.confetti.map((style, i) => (
              <span key={i} className="kuika-ai-chatbot-credits-success__confetti-piece" style={style} />
            ))}
          </div>

          <div className="kuika-ai-chatbot-credits-success__icon-wrap" aria-hidden="true">
            <KuikaModalIconWrapper iconName="check" intent="success" />
          </div>
          <KuikaH4 id={TITLE_ID} className="kuika-ai-chatbot-credits-success__title">
            You're all set!
          </KuikaH4>
          <p className="kuika-ai-chatbot-credits-success__message">
            <strong>{formatCreditAmount(addedCredits)}</strong> credits have been added to your workspace. Happy
            building!
          </p>
          {newBalance !== null && (
            <div className="kuika-ai-chatbot-credits-success__balance">
              <div className="kuika-ai-chatbot-credits-success__balance-label">New balance</div>
              <div className="kuika-ai-chatbot-credits-success__balance-value">
                {formatCreditAmount(newBalance)} credits
              </div>
            </div>
          )}
          <div className="kuika-ai-chatbot-credits-success__action">
            <KuikaButton type="primary" size="lg" onClick={onClose}>
              Back to the chatbot
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  }
}

export default AiChatbotSuccessModal;
